import React, { useMemo, useState, useSyncExternalStore } from "react";

import { getDataStore, getInstructeurById, countByDojo } from "@/data/dataStore.js";

import { DataTable } from "@/components/DataTable.jsx";

import { EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";

import { DojoCard } from "@/components/DojoCard.jsx";

import { InstructorCard } from "@/components/InstructorCard.jsx";

import { DemoNotice } from "@/components/DemoNotice.jsx";

import { PageSection } from "@/components/AppLayout.jsx";

import { EmptyState } from "@/coquille-bridge";

import { getMartialTitles, getLeadershipNote } from "@/content/martialTitlesI18n.js";

import { usePublicCopy } from "@/i18n/karatePublicI18n.js";

import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { isHybridDataMode } from "@/config/backendStaging049.js";

import { useKarateUi } from "@/hooks/useKarateUi.js";



function useDataStore() {

  return useSyncExternalStore(

    (cb) => {

      window.addEventListener("karate-data-updated", cb);

      return () => window.removeEventListener("karate-data-updated", cb);

    },

    () => getDataStore(),

    () => getDataStore(),

  );

}



function ViewToggle({ mode, onChange, labels }) {

  return (

    <div className="view-toggle" data-testid="view-toggle">

      <button

        type="button"

        className={mode === "cards" ? "view-toggle__btn active" : "view-toggle__btn"}

        onClick={() => onChange("cards")}

        data-testid="view-toggle-cards"

      >

        {labels.cards}

      </button>

      <button

        type="button"

        className={mode === "table" ? "view-toggle__btn active" : "view-toggle__btn"}

        onClick={() => onChange("table")}

        data-testid="view-toggle-table"

      >

        {labels.table}

      </button>

    </div>

  );

}



export function DojosPage() {

  const { copy, locale } = usePublicCopy();

  const d = copy.directory;

  const { localizeDemo, demoBadge, officialBadge, statusLabel } = useKarateUi();

  const { dojos } = useDataStore();

  const [view, setView] = useState("cards");

  const officialCount = dojos.filter((x) => !x.demo).length;



  const statusFilterOptions = useMemo(

    () =>

      ["actif", "inactif", "a_confirmer"].map((s) => ({

        value: s,

        label: statusLabel(s),

      })),

    [statusLabel],

  );



  const columns = useMemo(

    () => [

      { key: "nom", label: d.columns.name, render: (row) => localizeDemo(row.nom) },

      { key: "ville", label: d.columns.city, render: (row) => localizeDemo(row.ville) },

      { key: "adresse", label: d.columns.address, render: (row) => localizeDemo(row.adresse) },

      {

        key: "sensei_id",

        label: "Sensei",

        render: (row) => {

          const s = row.sensei_id ? getInstructeurById(row.sensei_id) : null;

          return s ? localizeDemo(s.nom) : "—";

        },

      },

      {

        key: "etudiants",

        label: d.columns.students,

        sortable: false,

        render: (row) => countByDojo(row.id),

      },

      {

        key: "demo",

        label: d.columns.type,

        render: (row) => (row.demo ? demoBadge : officialBadge),

      },

      {

        key: "statut",

        label: d.columns.status,

        render: (row) => <EntityStatusBadge statut={row.statut} />,

      },

    ],

    [d, localizeDemo, demoBadge, officialBadge],

  );



  return (

    <PageSection title={d.dojosTitle} subtitle={d.dojosSubtitle} testId="page-dojos">

      <div className="info-box" data-testid="dojos-official-wait">

        {d.dojosOfficialWait(officialCount, dojos.filter((x) => x.demo).length)}

      </div>



      <ViewToggle mode={view} onChange={setView} labels={{ cards: d.cards, table: d.table }} />



      {dojos.length === 0 ? (

        <EmptyState message={d.emptyDojos} testId="dojos-empty" />

      ) : view === "cards" ? (

        <div className="entity-card-grid" data-testid="dojos-cards">

          {dojos.map((dojo) => (

            <DojoCard

              key={dojo.id}

              dojo={dojo}

              senseiNom={

                dojo.sensei_id

                  ? localizeDemo(getInstructeurById(dojo.sensei_id)?.nom ?? "—")

                  : "—"

              }

              etudiantsCount={countByDojo(dojo.id)}

            />

          ))}

        </div>

      ) : (

        <DataTable

          rows={dojos}

          columns={columns}

          searchFields={["nom", "ville", "adresse"]}

          filters={[

            {

              field: "statut",

              label: d.columns.status,

              options: statusFilterOptions,

            },

            {

              field: "demo",

              label: d.columns.type,

              options: [

                { value: "true", label: demoBadge },

                { value: "false", label: officialBadge },

              ],

            },

          ]}

          testId="dojos-table"

        />

      )}

    </PageSection>

  );

}



export function InstructorsPage() {

  const { copy, locale } = usePublicCopy();

  const d = copy.directory;

  const { localizeDemo, demoBadge, statusLabel } = useKarateUi();

  const hybrid = useHybridDataStore();

  const { instructeurs, dojos } = hybrid.store;

  const avatarFromBackend = hybrid.backendStatus === "backend" || (isHybridDataMode() && hybrid.store._dataSource === "backend");

  const [view, setView] = useState("cards");

  const dojoMap = useMemo(

    () => Object.fromEntries(dojos.map((x) => [x.id, localizeDemo(x.nom)])),

    [dojos, localizeDemo],

  );

  const martialTitles = useMemo(() => getMartialTitles(locale), [locale]);

  const leadershipNote = useMemo(() => getLeadershipNote(locale), [locale]);



  const columns = useMemo(

    () => [

      { key: "nom", label: d.columns.name, render: (row) => localizeDemo(row.nom) },

      { key: "titre", label: d.columns.title },

      {

        key: "dojo_id",

        label: "Dojo",

        render: (row) => (row.dojo_id ? dojoMap[row.dojo_id] ?? row.dojo_id : d.unassigned),

      },

      { key: "courriel", label: d.columns.email },

      {

        key: "demo",

        label: d.columns.type,

        render: (row) => (row.demo ? demoBadge : d.typeReceived),

      },

      {

        key: "statut",

        label: d.columns.status,

        render: (row) => <EntityStatusBadge statut={row.statut} />,

      },

    ],

    [d, dojoMap, localizeDemo, demoBadge],

  );



  return (

    <PageSection title={d.instructorsTitle} subtitle={d.instructorsSubtitle} testId="page-instructeurs">

      <DemoNotice variant="card" />



      <section className="titles-explainer" data-testid="titles-explainer">

        <h2 className="section-h2">{d.martialTitlesTitle}</h2>

        <p className="muted">{leadershipNote}</p>

        <div className="titles-grid">

          {martialTitles.map((t) => (

            <article key={t.titre} className="title-card">

              <h3>{t.titre}</h3>

              <p className="title-card__level">{t.niveau}</p>

              <p>{t.description}</p>

            </article>

          ))}

        </div>

      </section>



      <ViewToggle mode={view} onChange={setView} labels={{ cards: d.cards, table: d.table }} />



      {view === "cards" ? (

        <div className="entity-card-grid" data-testid="instructeurs-cards">

          {instructeurs.map((i) => (

            <InstructorCard

              key={i.id}

              instructeur={i}

              dojoNom={i.dojo_id ? dojoMap[i.dojo_id] : undefined}

              avatarBackendAuthoritative={avatarFromBackend}

            />

          ))}

        </div>

      ) : (

        <DataTable

          rows={instructeurs}

          columns={columns}

          searchFields={["nom", "titre", "courriel"]}

          filters={[

            {

              field: "titre",

              label: d.columns.title,

              options: [...new Set(instructeurs.map((i) => i.titre))].map((t) => ({ value: t, label: t })),

            },

          ]}

          testId="instructeurs-table"

        />

      )}

    </PageSection>

  );

}



export function StudentsPage() {

  const { copy } = usePublicCopy();

  const d = copy.directory;

  const { localizeDemo, demoBadge, statusLabel } = useKarateUi();

  const { etudiants, dojos } = useDataStore();

  const dojoMap = useMemo(

    () => Object.fromEntries(dojos.map((x) => [x.id, localizeDemo(x.nom)])),

    [dojos, localizeDemo],

  );



  const statusFilterOptions = useMemo(

    () =>

      ["actif", "inactif", "a_confirmer"].map((s) => ({

        value: s,

        label: statusLabel(s),

      })),

    [statusLabel],

  );



  const columns = useMemo(

    () => [

      {

        key: "nom_complet",

        label: d.columns.name,

        render: (row) => (

          <>

            {localizeDemo(row.prenom)} {localizeDemo(row.nom)}

            {row.demo ? ` ${demoBadge}` : ""}

          </>

        ),

      },

      {

        key: "dojo_id",

        label: "Dojo",

        render: (row) => dojoMap[row.dojo_id] ?? row.dojo_id,

      },

      { key: "groupe", label: d.columns.group, render: (row) => localizeDemo(row.groupe) },

      { key: "ceinture", label: d.columns.belt, render: (row) => localizeDemo(row.ceinture) },

      {

        key: "statut",

        label: d.columns.status,

        render: (row) => <EntityStatusBadge statut={row.statut} />,

      },

    ],

    [d, dojoMap, localizeDemo, demoBadge],

  );



  const rows = etudiants
    .filter((e) => e.publicVisible !== false)
    .map((e) => ({ ...e, nom_complet: `${e.prenom} ${e.nom}` }));



  return (

    <PageSection title={d.studentsTitle} subtitle={d.studentsSubtitle} testId="page-etudiants">

      <div className="warning-box" data-testid="etudiants-official-wait">

        {d.studentsOfficialWait}

      </div>



      <DataTable

        rows={rows}

        columns={columns}

        searchFields={["prenom", "nom", "groupe", "ceinture", "nom_complet"]}
        emptyMessage={d.emptyStudents}
        emptyFilteredMessage={d.emptyStudentsFiltered}

        filters={[

          {

            field: "dojo_id",

            label: "Dojo",

            options: dojos.map((x) => ({ value: x.id, label: localizeDemo(x.nom) })),

          },

          {

            field: "statut",

            label: d.columns.status,

            options: statusFilterOptions,

          },

        ]}

        testId="etudiants-table"

      />

    </PageSection>

  );

}


